/**
 * WAVE 3 ITEM 4, the SCREEN's half of the staged presses (William, 2 Oct 2026, his Care page: "Both
 * take effect once approved. and, all items go to approval then take effect."; the contract is
 * lanes\po-0915\26.10.02 02.40.00 AM, rev1, Wave 3 Item 4 - The Approval Contract).
 *
 * With the deployment's MEMBERCARE_STAGED_PRESSES on (the screen learns it as features.stagedPresses,
 * read with the client list), a press bound to ONE case (a reply press, a key, Send to Main Dock,
 * Close file) is QUEUED on the case and takes effect when the case is approved. The 01A grid's Approve,
 * Approve opt out and Approve STOP ARE the approval and act at once (they alone send `approval: true`).
 * With the switch off no answer ever says staged, so nothing below is reached and every screen is what
 * it was.
 *
 * The words and the small rules the screen needs, pure and in ONE place, so the drawer, the keys and
 * the press bars all say the same thing. A queued answer is NEVER worded as done. NOTHING here sends
 * anything to a member.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
import type { PostResult } from '@lib/apiClient';
import type { EventAction, PendingPress } from '@lib/care-model';
import { REPLY_OP_WORDING, type ReplyOp } from '@lib/reply-groups';
import { QUICK_KEY_ROWS } from '@lib/quick-keys';

/** How every queued answer starts on the screen (contract, The screen, item 3). */
export const QUEUED_PREFIX = 'Queued for approval';

/** The contract's sentence for one queued press, in the press's own words. */
export function queuedSentence(opWords: string): string {
  return `${QUEUED_PREFIX}: ${opWords}; it takes effect when the case is approved.`;
}

/** Several entries queued by one press of a bar: how many of how many, never a done word. */
export function queuedCountSentence(label: string, staged: number, total: number): string {
  return `${QUEUED_PREFIX}: ${label}, ${staged} of ${total}; each takes effect when its case is approved.`;
}

/**
 * A press the server took as one to QUEUE (its answer says `staged: true`) that queued nothing, every
 * entry refused: said as such, with no done word for a press that could never have been done.
 */
export function nothingQueuedSentence(label: string, total: number): string {
  return `Nothing was queued for approval: ${label}, 0 of ${total}.`;
}

export const STAGED_WORDING = {
  /** Close file, the answer says staged (contract, The screen, item 1, ON). */
  closeFileQueued: `${QUEUED_PREFIX}: the case closes when it is approved.`,
  /** The words of a pending Close file in the Waiting for approval block. */
  closeFileOp: 'Close file (answered, no further follow-up)',
  /** The Waiting for approval block (contract, The screen, item 2). */
  blockHead: 'Waiting for approval',
  blockAux: (n: number): string => `${n} press${n === 1 ? '' : 'es'} queued`,
  blockNote: 'Nothing below has taken effect. Each takes effect when the case is approved; a press that is refused then is listed with its reason, never dropped.',
  withdraw: 'Withdraw',
  withdrawing: 'Withdrawing...',
  withdrawHelp: 'Take this press off the case. It will not take effect.',
  withdrawn: (opWords: string): string => `Withdrawn: ${opWords}. It will not take effect.`,
  /** In review the reviewer acts: the server refuses a withdraw (409 in_review). */
  inReview: 'In review: to withdraw a press, Recall from review first (or the reviewer returns the case to the queue).',
  inReviewRefused: 'Not withdrawn: the case is in review. Recall from review first (or the reviewer returns it to the queue), then withdraw.',
  /** A case another operator holds: the list is shown, Withdraw is not. */
  lockedNote: 'Only the person handling the case can withdraw a press.',
  /** A finished case that still lists a press (an approval that stopped short): nothing to withdraw. */
  finishedNote: 'This case is finished, so a queued press can no longer be withdrawn here.',
  /** A key whose queueing lost a race with another save of the case: nothing was queued, press again. */
  keyConflict: 'Not queued: the case changed while you pressed (someone else saved it). Press again.',
  /** Contract, The screen, item 5: the guard's own words. */
  guardWords: 'Send to review first, or untick them',
  /**
   * The guard's sentence: how many ticks are not saved, then the contract's words as written. In review
   * the dispositions are frozen, so it says how to get to either.
   */
  guard: (n: number, inReview = false): string =>
    `Ticked dispositions are not saved yet (${n}), so a press waits. Send to review first, or untick them${inReview ? ' (Recall from review to do either)' : ''}.`,
  /** The line a press's confirm box adds while presses are staged, so nobody expects it to act at once. */
  confirmQueued: 'This press is queued for approval: it takes effect when the case is approved, not now.',
  /**
   * L2 (William, 16 Oct 2026 about 10 PM: "opt-in waits for review"). The opt back in in the disposition
   * list: its words in the Waiting for approval block, and the line under its box.
   */
  optInAction: 'Opt-in (texting opens again)',
  optInWaits: 'Waits for Final Review: texting opens again only when a reviewer approves the case. Nothing changes before then.',
  /** One reply press per case at a time: the second waits for a Withdraw. */
  replyQueued: (opWords: string): string => `Already queued for approval: ${opWords}. Withdraw it below to press another.`,
  /** A queued Close file: a second one is never offered. */
  closeQueued: 'Close file is already queued for approval: the case closes when it is approved.',
  /** The reviewer's Approve with nothing queued: the sentence the drawer has always said. */
  approved: 'Approved. Resolution emitted to the Portal.',
  /** What the reviewer's Approve adds when the case carried queued presses and the answer does not list them. */
  approvedPresses: (n: number): string =>
    `The ${n} queued press${n === 1 ? '' : 'es'} ran at this approval: each is in the action log as applied or refused, with the reason.`,
  /** The same, counted off the answer: how many were applied, and each one refused, by name, with its reason. */
  settled: (applied: number, refused: readonly string[]): string =>
    `Queued presses at approval: ${applied} applied${refused.length ? `, ${refused.length} REFUSED (${refused.join('; ')})` : ''}. Each is in the action log of its case.`,
  /** Final review, after a batch: the cases a queued Close file closed sent no resolution. */
  batchClosed: (n: number): string =>
    `${n} case${n === 1 ? ' was' : 's were'} closed by a queued Close file (answered, no further follow-up): no resolution went to the Portal for ${n === 1 ? 'it' : 'them'}.`,
  /** Final review, above the table: the ticked cases carry queued presses, which an approval runs. */
  batchHeadsUp: (presses: number, cases: number): string =>
    `${presses} queued press${presses === 1 ? '' : 'es'} on ${cases} ticked case${cases === 1 ? '' : 's'}: approving runs ${presses === 1 ? 'it' : 'them'}. Open a case to see what is queued.`,
  /** The reviewer's Approve on a queued Close file: the case closes, no resolution goes to the Portal. */
  approvedClosed: 'Approved: the case is closed (answered, no further follow-up). No resolution went to the Portal and nothing was sent to the member.',
  /** What the action log calls the two rows an approval leaves for each queued press. */
  applied: 'Queued press applied at approval',
  refused: 'Queued press REFUSED at approval',
  /** The queue's mark on a row that carries queued presses. */
  rowPill: (n: number): string => `${n} queued`,
  rowPillHelp: (n: number): string =>
    `${n} press${n === 1 ? '' : 'es'} on this case wait${n === 1 ? 's' : ''} for approval and take${n === 1 ? 's' : ''} effect when it is approved. Open the case to see ${n === 1 ? 'it' : 'them'}.`,
  /** William, 22 Oct 2026: Final Review's Actions pill for a waiting press, and what it says on hover. */
  queuedOpHelp: 'Queued: runs on Approve',
  /** The Actions slicer's name for a waiting press, beside the resolved actions. */
  queuedOpSlicer: (word: string): string => `${word} (queued)`,
  /** Final Review's approve button (a pill): "Approve (11)". */
  approveButton: (n: number): string => `Approve (${n})`,
} as const;

/**
 * I1 a (William, 17 Oct 2026): THE WORDS FOR A STOP THAT WAITS. The server stages every Opt Out and Stop press, and
 * records every Opt-out of program and Do Not Contact as a stop that waits, whatever the staging switch says
 * (api/src/lib/stop-approval.ts, ACTS_AT_ONCE is empty). Care's texting block goes on at once, PROVISIONAL; the
 * stop itself waits for approval in Final Review. Approval makes it final; archiving the case from Final Review,
 * or withdrawing the press, lifts the block. One table, so the drawer, its keys and the queue bar say the same.
 * NOT for the 01A grid's Approve opt out and Approve STOP: those ARE the approval and act at once.
 */
export const STOP_WAITS_WORDING = {
  /** A press (the case's Opt Out, a key, the queue bar's Opt-Out): the block goes on now. */
  optOut: 'The texting block goes on now. The opt-out waits for approval in Final Review: approval makes it final; archiving the case from Final Review or withdrawing the press lifts the block.',
  stop: 'The texting block goes on now. The stop waits for approval in Final Review: approval makes it final; archiving the case from Final Review or withdrawing the press lifts the block. A STOP the member texted is never lifted by Care.',
  /** A disposition (Opt-out of program, Do Not Contact): recorded when the case is sent to Final Review. */
  optOutDisposition: 'The texting block goes on as soon as you send the case. The opt-out on the member\'s file waits for approval in Final Review: approval makes it final; archiving the case from Final Review lifts the block.',
  dncDisposition: 'The block goes on the master list as soon as you send the case. The member\'s record is written when Final Review approves the case: approval makes it final; archiving the case from Final Review lifts the block.',
} as const;

/**
 * I2 a (William, 17 Oct 2026): the drawer's sentence after a Withdraw. The press never takes effect, and each texting
 * block it had put on is said: lifted, nothing to lift, or kept and why, or NOT lifted and why. Codes only in, words
 * out; one sentence per outcome, in a fixed order, so two blocks with the same outcome are said once.
 */
export function withdrawnSentence(opWords: string, stops: ReadonlyArray<{ result: string; code: string; heldFor?: string | undefined }> | null | undefined): string {
  const base = STAGED_WORDING.withdrawn(opWords);
  const list = stops ?? [];
  if (!list.length) return base;
  const parts: string[] = [];
  const has = (code: string) => list.some((s) => s.code === code);
  if (list.some((s) => s.result === 'lifted')) parts.push('The texting block it put on is lifted.');
  if (has('nothing-to-lift')) parts.push("It had put no texting block of Care's on, so there was nothing to lift.");
  if (has('kept-keyword')) parts.push("The texting block stays: the member texted STOP, and only the member's own START reopens texts.");
  if (has('kept-for-press') || has('kept-for-stop')) parts.push('The texting block stays: another stop waiting on this case still needs it, and it now goes with that stop at approval.');
  // QA-1651 F7 (William, 17 Oct 2026): an approved, kept or applied stop on this same case still needs the row.
  if (has('kept-for-this-case')) parts.push('The texting block stays: another stop on this case (approved, or kept after its press failed) still needs it.');
  // QA-1652 C3 (William, 17 Oct 2026): a block a failed press left on is never lifted unless it is named.
  if (has('kept-press-failed')) parts.push('The texting block stays: its press failed at approval, so it stays on until the stop is pressed again or the block is lifted by hand.');
  const forCase = list.find((s) => s.code === 'kept-for-case');
  if (forCase) parts.push(`The texting block stays: another case on this phone${forCase.heldFor ? ` (${forCase.heldFor})` : ''} still needs it (an approved or waiting stop, or the member's own STOP).`);
  if (has('failed-other-cases-unread')) parts.push('The texting block is NOT lifted: the other cases on this phone could not be checked, so nothing was asked of the platform. It is still on; lift it by hand from the Do not contact table once checked.');
  const failed = list.filter((s) => s.result === 'failed' && s.code !== 'failed-other-cases-unread');
  if (failed.length) parts.push(`The texting block could NOT be lifted (${[...new Set(failed.map((s) => s.code.replace(/^failed-/, '')))].join(', ')}): it is still on. Lift it by hand from the Do not contact table.`);
  return [base, ...parts].join(' ');
}

/**
 * The help of a reply press that QUEUES (the case's own reply buttons and its keys), I1 a: an Opt Out and a Stop
 * say that the block goes on now and the rest waits; an Opt In and Send to Main Dock keep their own words.
 */
export function replyPressHelp(op: ReplyOp): string {
  if (op === 'opt-out') return `Opt the member out of THIS campaign only (the member's file and the campaign's opt-out list). ${STOP_WAITS_WORDING.optOut}`;
  if (op === 'stop') return `The member texted STOP: do not contact on the member's file. ${STOP_WAITS_WORDING.stop}`;
  return REPLY_OP_WORDING[op].help;
}

/** True for the two reply presses that are stops (their block goes on now; the rest waits for approval). */
export function isStopPress(op: string): op is 'opt-out' | 'stop' {
  return op === 'opt-out' || op === 'stop';
}

/**
 * The contract's screen item 4: the ops of the 01A grid's three GREEN presses (Approve = opt-in, Approve
 * opt out = opt-out, Approve STOP = stop). Pressed FROM THAT GRID they are the approval and say so on the
 * wire (`approval: true`); Send to Main Dock (refer) never is. The grid is the only caller: the Inbound
 * queue's bar and the case's own reply presses send the same ops WITHOUT the flag, so they are queued.
 */
export function isApprovalPress(op: string): boolean {
  return op === 'opt-in' || op === 'opt-out' || op === 'stop';
}

/* ------------------------------------------ the words of a press ------------------------------------------ */

const REPLY_OPS_ALL: readonly ReplyOp[] = ['opt-in', 'opt-out', 'stop', 'refer'];
const isReplyOp = (op: string): op is ReplyOp => (REPLY_OPS_ALL as readonly string[]).includes(op);

/** The words of a key's door op: the key's own label ("Unstop", "Change Language"); the op itself when unknown. */
function keyWords(op: string): string {
  for (const row of QUICK_KEY_ROWS) for (const k of row.keys) if (k.id === op) return k.label;
  return op;
}

/**
 * What a press is called on the screen, from its kind and op alone (the body is never read for words:
 * it may hold an address or a reason, and the block shows no more than the press's name).
 */
export function pressWords(kind: string, op: string): string {
  if (kind === 'close-file' || op === 'close-file') return STAGED_WORDING.closeFileOp;
  if (kind === 'reply' && isReplyOp(op)) return REPLY_OP_WORDING[op].button;
  if (kind === 'key') return keyWords(op);
  if (kind === 'action' && op === 'opt-in') return STAGED_WORDING.optInAction;
  // An older or a later kind: the reply table, then the keys, then the op as sent. Never a blank.
  if (isReplyOp(op)) return REPLY_OP_WORDING[op].button;
  return keyWords(op) || kind || 'press';
}

export function pendingWords(p: Pick<PendingPress, 'kind' | 'op'>): string {
  return pressWords(String(p.kind ?? ''), String(p.op ?? ''));
}

/** "sherry.east@claridynamics.com" -> "Sherry East", for a press that carries no name. */
function nameOfAddress(address: string): string {
  const local = address.split('@')[0] ?? '';
  return local.split(/[._-]+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || address;
}

/** Who pressed, in the shortest true words: the name the server stamped, else the name off the address. */
export function pendingWho(p: Pick<PendingPress, 'by' | 'byName'>): string {
  const name = String(p.byName ?? '').trim();
  if (name) return name;
  const by = String(p.by ?? '').trim();
  return by ? nameOfAddress(by) : 'unknown';
}

/** The record's pending list, as an array whatever the server sent (absent on a server before this build). */
export function pendingOf(ev: { pending?: PendingPress[] | null | undefined } | null | undefined): PendingPress[] {
  const list = ev?.pending;
  return Array.isArray(list) ? list.filter((p) => !!p && typeof p === 'object' && typeof p.id === 'string' && p.id !== '') : [];
}

/** The list row's count of queued presses: a whole number, 0 for anything else (an older server sends none). */
export function pendingCountOf(r: { pendingCount?: number | null | undefined }): number {
  const n = r.pendingCount;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : 0;
}

/** The list row's names of the waiting presses (pendingOps); [] when absent (an older server) or none wait. */
export function pendingOpsOf(r: { pendingOps?: unknown }): string[] {
  const v = r.pendingOps;
  return Array.isArray(v) ? v.filter((w): w is string => typeof w === 'string' && w.trim() !== '') : [];
}

/* ------------------------------------------ reading an answer ------------------------------------------ */

/**
 * A reply press's row was QUEUED, not done. The contract names the row's `outcome: 'staged'`; a row
 * whose status says staged is read the same way, so neither spelling is ever counted as done.
 */
export function isStagedRow(r: unknown): boolean {
  const row = (r ?? {}) as { outcome?: unknown; status?: unknown };
  return row.outcome === 'staged' || row.status === 'staged';
}

/** The first string among the candidates that is already a queued sentence, or ''. */
function queuedTextOf(...candidates: unknown[]): string {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().startsWith(QUEUED_PREFIX)) return c.trim();
  }
  return '';
}

/**
 * One queued reply row's sentence: the server's own when the row carries one, else the contract's
 * sentence built from the words on the button she pressed. Either way it starts "Queued for approval".
 */
export function stagedRowSentence(r: unknown, label: string): string {
  const row = (r ?? {}) as { sentence?: unknown; message?: unknown; reason?: unknown };
  return queuedTextOf(row.sentence, row.message, row.reason) || queuedSentence(label);
}

/** A key's answer says the press was queued (`staged: true`). Read before anything else of the answer. */
export function isStagedAnswer(r: PostResult<unknown>): boolean {
  return r.ok && (r.data as { staged?: unknown } | null | undefined)?.staged === true;
}

/**
 * A queued key's sentence: the server's own when it already says queued, else the contract's sentence
 * with the key's name. A done word from the answer is never printed for a queued press.
 */
export function stagedKeySentence(r: PostResult<unknown>, label: string): string {
  const d = (r.data ?? {}) as { message?: unknown; sentence?: unknown };
  return queuedTextOf(d.sentence, d.message) || queuedSentence(label);
}

/** The server refused a withdraw because the case is in review (409 in_review). */
export function isInReviewRefusal(r: PostResult<unknown>): boolean {
  const d = (r.data ?? {}) as { error?: unknown; code?: unknown };
  return r.status === 409 && (d.error === 'in_review' || d.code === 'in_review');
}

/** One queued press as an approval's answer reports it (the server's AppliedPress; `eventId` on a batch). */
export interface SettledPress {
  eventId: string;
  kind: string;
  op: string;
  applied: boolean;
  reason: string;
}

/**
 * What an approval's answer says it did with the queued presses: the list under `pending` (the single
 * approve and the batch approve both answer one). null when the answer carries no list, which is every
 * approval of a case with nothing queued; a line that is not an object is dropped, never guessed at.
 */
export function settledOf(answer: unknown): SettledPress[] | null {
  const list = (answer as { pending?: unknown } | null | undefined)?.pending;
  if (!Array.isArray(list)) return null;
  return list
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    .map((x) => ({
      eventId: typeof x.eventId === 'string' ? x.eventId : '',
      kind: String(x.kind ?? ''),
      op: String(x.op ?? ''),
      applied: x.applied === true,
      reason: typeof x.reason === 'string' ? x.reason.trim() : '',
    }));
}

/**
 * The settled presses in one sentence: how many were applied, and EACH refused one by name with its
 * reason (and its case, on a batch). A refusal is never folded into a count alone. '' for an empty list.
 */
export function settledSentence(settled: readonly SettledPress[]): string {
  if (!settled.length) return '';
  const refused = settled.filter((x) => !x.applied)
    .map((x) => `${x.eventId ? `${x.eventId} ` : ''}${pressWords(x.kind, x.op)}: ${x.reason || 'no reason given'}`);
  return STAGED_WORDING.settled(settled.length - refused.length, refused);
}

/**
 * Final review's batch approve: what the approvals did with the queued presses, and how many cases a
 * queued Close file closed with no resolution (the page's own "Approved" would otherwise read as one
 * resolution per case). '' when the answer lists none, which is every batch with nothing queued.
 */
export function batchSettledSentence(answer: unknown): string {
  const settled = settledOf(answer) ?? [];
  if (!settled.length) return '';
  const closed = settled.filter((x) => x.kind === 'close-file' && x.applied).length;
  return [settledSentence(settled), closed ? STAGED_WORDING.batchClosed(closed) : ''].filter(Boolean).join(' ');
}

/**
 * The reviewer's Approve in words, read off the answer first: what the case became, and what the
 * approval did with each queued press (applied, or refused with its reason). With nothing queued it is
 * the sentence the drawer has always said. A case the answer says is `closed-no-action` was closed by
 * its queued Close file (no resolution for it), so the sentence never claims a resolution went to the
 * Portal. `queued` is what the drawer listed when Approve was pressed, used only when the answer
 * carries no list of its own.
 */
export function approvedSentence(queued: readonly Pick<PendingPress, 'kind'>[], answer: { status?: unknown; pending?: unknown } | null | undefined): string {
  const settled = settledOf(answer);
  if (settled && settled.length) {
    const closedByFile = settled.some((x) => x.kind === 'close-file' && x.applied);
    const closed = answer?.status === 'closed-no-action' || (closedByFile && answer?.status !== 'cleared');
    const others = settled.filter((x) => x.kind !== 'close-file' || !x.applied);
    const base = closed ? STAGED_WORDING.approvedClosed : STAGED_WORDING.approved;
    return others.length ? `${base} ${settledSentence(others)}` : base;
  }
  if (!queued.length) return STAGED_WORDING.approved;
  const closes = queued.filter((p) => p.kind === 'close-file').length;
  if (answer?.status === 'closed-no-action' || (closes > 0 && answer?.status !== 'cleared')) {
    const others = queued.length - closes;
    return others > 0 ? `${STAGED_WORDING.approvedClosed} ${STAGED_WORDING.approvedPresses(others)}` : STAGED_WORDING.approvedClosed;
  }
  return `${STAGED_WORDING.approved} ${STAGED_WORDING.approvedPresses(queued.length)}`;
}

/* ------------------------------------------ what an approval left ------------------------------------------ */

/** The two action rows an approval records per queued press (contract, At approval). */
export const PRESS_APPLIED = 'press-applied';
export const PRESS_REFUSED = 'press-refused';

export function isPressOutcomeAction(a: { type?: unknown } | null | undefined): boolean {
  const t = String(a?.type ?? '');
  return t === PRESS_APPLIED || t === PRESS_REFUSED;
}

/**
 * One line of the action log for a press an approval ran: what it was, whether it was applied or
 * refused (with the reason), and who pressed it. Reads only the fields the contract names on the row.
 */
export function pressOutcomeLine(a: Pick<EventAction, 'data'> & { type: string }): { label: string; refused: boolean; detail: string } {
  const d = (a.data ?? {}) as { kind?: unknown; op?: unknown; outcome?: unknown; reason?: unknown; pressedBy?: unknown };
  const refused = a.type === PRESS_REFUSED;
  const words = pressWords(String(d.kind ?? ''), String(d.op ?? ''));
  const pressedBy = typeof d.pressedBy === 'string' && d.pressedBy.trim() ? nameOfAddress(d.pressedBy.trim()) : '';
  const why = refused ? String(d.reason ?? '').trim() : '';
  const detail = [words, refused ? (why || 'no reason given') : '', pressedBy ? `pressed by ${pressedBy}` : ''].filter(Boolean).join(' · ');
  return { label: refused ? STAGED_WORDING.refused : STAGED_WORDING.applied, refused, detail };
}
